import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, IconButton, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as BackIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import {
    useVelikostTypyQuery,
    useCreateVelikostTypMutation,
    useUpdateVelikostTypMutation,
    useDeleteVelikostTypMutation,
} from '../../api/JsdhNotifikaceService/service/InventarMutations';
import type { VelikostPole } from '../../api/JsdhNotifikaceService/generated/models/Inventar';

const InventarVelikostTypyPage: React.FC = () => {
    const navigate = useNavigate();
    const { data: typy, isLoading } = useVelikostTypyQuery();
    const createMutation = useCreateVelikostTypMutation();
    const updateMutation = useUpdateVelikostTypMutation();
    const deleteMutation = useDeleteVelikostTypMutation();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingPoradi, setEditingPoradi] = useState<number | null>(null);
    const [formNazev, setFormNazev] = useState('');
    const [formPole, setFormPole] = useState<VelikostPole[]>([{ label: '', hodnoty: [] }]);
    const [newValue, setNewValue] = useState<string[]>(['']);

    const resetForm = () => {
        setFormNazev('');
        setFormPole([{ label: '', hodnoty: [] }]);
        setNewValue(['']);
        setEditingId(null);
        setEditingPoradi(null);
    };

    const openCreate = () => {
        resetForm();
        setDialogOpen(true);
    };

    const openEdit = (typ: any) => {
        setEditingId(typ.id);
        setEditingPoradi(typ.poradi);
        setFormNazev(typ.nazev);
        try {
            const pole: VelikostPole[] = JSON.parse(typ.pole);
            setFormPole(pole);
            setNewValue(pole.map(() => ''));
        } catch {
            setFormPole([{ label: '', hodnoty: [] }]);
            setNewValue(['']);
        }
        setDialogOpen(true);
    };

    const handleAddPole = () => {
        if (formPole.length < 2) {
            setFormPole([...formPole, { label: '', hodnoty: [] }]);
            setNewValue([...newValue, '']);
        }
    };

    const handleRemovePole = (index: number) => {
        setFormPole(formPole.filter((_, i) => i !== index));
        setNewValue(newValue.filter((_, i) => i !== index));
    };

    const handlePoleLabel = (index: number, label: string) => {
        const updated = [...formPole];
        updated[index] = { ...updated[index], label };
        setFormPole(updated);
    };

    const handleAddHodnota = (poleIndex: number) => {
        const val = newValue[poleIndex]?.trim();
        if (!val) return;
        const updated = [...formPole];
        if (!updated[poleIndex].hodnoty.includes(val)) {
            updated[poleIndex] = { ...updated[poleIndex], hodnoty: [...updated[poleIndex].hodnoty, val] };
            setFormPole(updated);
        }
        const nv = [...newValue];
        nv[poleIndex] = '';
        setNewValue(nv);
    };

    const handleRemoveHodnota = (poleIndex: number, hodnotaIndex: number) => {
        const updated = [...formPole];
        updated[poleIndex] = {
            ...updated[poleIndex],
            hodnoty: updated[poleIndex].hodnoty.filter((_, i) => i !== hodnotaIndex),
        };
        setFormPole(updated);
    };

    const handleSubmit = () => {
        if (!formNazev || formPole.some(p => !p.label || p.hodnoty.length === 0)) return;
        const data = {
            nazev: formNazev,
            pole: JSON.stringify(formPole),
            // Update nesmí posílat undefined — BE by zapsal NULL a typ by skočil na začátek
            poradi: editingId ? (editingPoradi ?? undefined) : (typy?.length ?? 0) + 1,
        };
        if (editingId) {
            updateMutation.mutate({ id: editingId, data }, { onSuccess: () => { setDialogOpen(false); resetForm(); } });
        } else {
            createMutation.mutate(data, { onSuccess: () => { setDialogOpen(false); resetForm(); } });
        }
    };

    const parsePole = (poleJson: string): VelikostPole[] => {
        try { return JSON.parse(poleJson); } catch { return []; }
    };

    return (
        <Box>
            <PageHeader
                title="Typy velikostí"
                icon={<BackIcon sx={{ cursor: 'pointer' }} onClick={() => navigate('/admin/inventar')} />}
                action={
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                        Nový typ
                    </Button>
                }
            />

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Název</TableCell>
                                    <TableCell>Pole</TableCell>
                                    <TableCell width={100} align="right">Akce</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {typy?.map((typ: any) => {
                                    const pole = parsePole(typ.pole);
                                    return (
                                        <TableRow key={typ.id}>
                                            <TableCell><Typography fontWeight={500}>{typ.nazev}</Typography></TableCell>
                                            <TableCell>
                                                {pole.map((p, i) => (
                                                    <Box key={i} sx={{ mb: pole.length > 1 && i === 0 ? 0.5 : 0 }}>
                                                        <Typography variant="caption" color="text.secondary">{p.label}:</Typography>{' '}
                                                        {p.hodnoty.map((h, j) => (
                                                            <Chip key={j} label={h} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                                                        ))}
                                                    </Box>
                                                ))}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => openEdit(typ)}><EditIcon fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => {
                                                    if (window.confirm(`Smazat typ "${typ.nazev}"?`)) deleteMutation.mutate(typ.id);
                                                }}><DeleteIcon fontSize="small" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {(!typy || !typy.length) && (
                                    <TableRow><TableCell colSpan={3} align="center">Žádné typy velikostí</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Upravit typ velikosti' : 'Nový typ velikosti'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Název typu"
                            required
                            value={formNazev}
                            onChange={(e) => setFormNazev(e.target.value)}
                            placeholder="Např. Konfekce, Číslo boty"
                        />

                        {formPole.map((pole, poleIndex) => (
                            <Paper key={poleIndex} variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">Pole {poleIndex + 1}</Typography>
                                    {formPole.length > 1 && (
                                        <IconButton size="small" onClick={() => handleRemovePole(poleIndex)}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                                <TextField
                                    label="Název pole"
                                    required
                                    size="small"
                                    fullWidth
                                    value={pole.label}
                                    onChange={(e) => handlePoleLabel(poleIndex, e.target.value)}
                                    placeholder="Např. Velikost, Výška, Číslo"
                                    sx={{ mb: 1 }}
                                />
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                    {pole.hodnoty.map((h, hIndex) => (
                                        <Chip
                                            key={hIndex}
                                            label={h}
                                            size="small"
                                            onDelete={() => handleRemoveHodnota(poleIndex, hIndex)}
                                        />
                                    ))}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        size="small"
                                        placeholder="Přidat hodnotu..."
                                        value={newValue[poleIndex] ?? ''}
                                        onChange={(e) => {
                                            const nv = [...newValue];
                                            nv[poleIndex] = e.target.value;
                                            setNewValue(nv);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') { e.preventDefault(); handleAddHodnota(poleIndex); }
                                        }}
                                        sx={{ flex: 1 }}
                                    />
                                    <Button size="small" variant="outlined" onClick={() => handleAddHodnota(poleIndex)}>
                                        Přidat
                                    </Button>
                                </Box>
                            </Paper>
                        ))}

                        {formPole.length < 2 && (
                            <Button size="small" startIcon={<AddIcon />} onClick={handleAddPole}>
                                Přidat druhé pole (např. výška)
                            </Button>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDialogOpen(false); resetForm(); }}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!formNazev || formPole.some(p => !p.label || p.hodnoty.length === 0)}
                    >
                        {editingId ? 'Uložit' : 'Vytvořit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InventarVelikostTypyPage;
